import { API_URL } from './api';

// Sender-chosen access for a photo / video / document (mirrors the backend).
export const VIEW_PRINT = 'VIEW_PRINT'; // receiver can only view + print
export const VIEW_PRINT_DOWNLOAD = 'VIEW_PRINT_DOWNLOAD'; // default, old messages
const RESTRICTABLE = ['image', 'video', 'document'];

export function normalizePermission(v) {
  return v === VIEW_PRINT ? VIEW_PRINT : VIEW_PRINT_DOWNLOAD;
}

export function isViewOnly(message) {
  return (
    message.downloadPermission === VIEW_PRINT && RESTRICTABLE.includes(message.type)
  );
}

// GridFS / view-only URLs come back as API-relative paths
// (/api/posts/file/…, /api/media/…) — resolve them against the API host.
export function resolveUrl(url) {
  if (!url) return null;
  if (url.startsWith('/')) return `${API_URL}${url}`;
  return url;
}

const IMAGE_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
const TEXT_EXT = ['txt', 'csv', 'json', 'xml', 'md', 'log', 'yaml', 'yml', 'ini'];

export function extensionOf(fileName, url) {
  const pick = (s) => {
    const m = /\.([a-z0-9]+)$/i.exec(s || '');
    return m ? m[1].toLowerCase() : '';
  };
  let ext = pick(fileName);
  if (!ext && url) {
    try {
      ext = pick(new URL(url).pathname);
    } catch {
      /* ignore */
    }
  }
  return ext;
}

// What the in-app viewer can render for a message: 'image' | 'video' | 'pdf' |
// 'text' | null (no in-app preview).
export function viewerKind(message) {
  if (message.type === 'image') return 'image';
  if (message.type === 'video') return 'video';
  const ext = extensionOf(message.fileName, message.fileUrl);
  if (ext === 'pdf') return 'pdf';
  if (IMAGE_EXT.includes(ext)) return 'image';
  if (TEXT_EXT.includes(ext)) return 'text';
  return null;
}

export async function fetchBlob(url) {
  const res = await fetch(url, { mode: 'cors', cache: 'no-store' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

// Prints HTML in a hidden iframe (so the user only gets the print dialog, not
// a new tab with the raw file). The iframe is removed after printing.
export function printHtml(bodyHtml, title = 'Print') {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument;
  doc.open();
  doc.write(`<!doctype html><html><head><title>${escapeHtml(title)}</title><style>
    @page { margin: 12mm; }
    body { margin: 0; font-family: system-ui, sans-serif; }
    img { display: block; max-width: 100%; margin: 0 auto; page-break-after: always; }
    img:last-child { page-break-after: auto; }
    pre { white-space: pre-wrap; word-break: break-word; font-size: 11px; }
  </style></head><body>${bodyHtml}</body></html>`);
  doc.close();

  const images = Array.from(doc.images);
  const ready = Promise.all(
    images.map((img) =>
      img.complete ? null : new Promise((r) => { img.onload = img.onerror = r; })
    )
  );
  ready.then(() => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => iframe.remove(), 1000);
  });
}

export function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}
